/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import type { EuiBreadcrumb } from '@elastic/eui'
import { EuiHeaderBreadcrumbs } from '@elastic/eui'

import type { RoutableBreadcrumb } from '@modules/ui-types'
import { withSmallErrorBoundary } from '@modules/cui/Boundary'
import history from '@modules/utils/history'

import BreadcrumbsWrapper from './BreadcrumbsWrapper'

import type { FunctionComponent } from 'react'

type Props = {
  breadcrumbs?: RoutableBreadcrumb[]
  linkAll?: boolean
  truncate?: boolean
}

const BreadcrumbsImpl: FunctionComponent<Props> = ({
  breadcrumbs = [],
  linkAll,
  truncate = true,
}) => {
  const euiBreadcrumbs = breadcrumbs.map(
    ({ text, to }: RoutableBreadcrumb, index: number, list: RoutableBreadcrumb[]) => {
      const isLast = index === list.length - 1
      const linkify = linkAll || !isLast

      return getEuiBreadcrumb({ text, href: to, linkify })
    },
  )

  return (
    <BreadcrumbsWrapper>
      <EuiHeaderBreadcrumbs
        arial-label='breadcrumb'
        data-test-id='populated-header-breadcrumbs'
        breadcrumbs={euiBreadcrumbs}
        truncate={truncate}
      />
    </BreadcrumbsWrapper>
  )
}

export default withSmallErrorBoundary(BreadcrumbsImpl)

function getEuiBreadcrumb({
  text,
  href,
  linkify,
}: {
  text: RoutableBreadcrumb['text']
  href?: string
  linkify: boolean
}): EuiBreadcrumb {
  const linkProps = linkify
    ? {
        href,
        onClick: href
          ? (e) => {
              history.push(href)
              e.preventDefault()
              e.stopPropagation()
            }
          : undefined,
      }
    : {}

  return {
    text,
    ...linkProps,
  }
}
